/**
 * JORelatedProductsInjector — Injects two sections below the product detail page:
 *   1. "Vous aimerez aussi" — 4 products from the same category
 *   2. "Vus récemment"       — up to 4 products the visitor recently browsed
 *
 * ALSO stores the currently visited product slug into localStorage so
 * "Récemment consultés" builds up as the user browses.
 *
 * NON-CORE — additive DOM injector. Uses createPortal to mount inside a
 * placeholder appended to <main> on any /product/:slug or /produit/:slug page.
 *
 * Mounted globally from App.js.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "msc_recent_products";
const MAX_RECENT = 12;
const ANCHOR_ID = "jo-related-products-anchor";

function getRecent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) || []) : [];
  } catch {
    return [];
  }
}

function pushRecent(slug) {
  if (!slug) return;
  const list = getRecent().filter((s) => s !== slug);
  list.unshift(slug);
  const trimmed = list.slice(0, MAX_RECENT);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    /* ignore quota */
  }
}

function fmtEur(cents) {
  return `${(Number(cents || 0) / 100).toFixed(2)} €`;
}

function useProductRoute(pathname) {
  // Cockpit product detail URL is /shop/:slug (not /product/:slug).
  // Match /shop/<slug> but NOT the plain /shop listing page.
  const m = pathname.match(/^\/shop\/([^/?#]+)$/);
  return m ? m[1] : null;
}

/** Find the ProductPage's main section (`section` under public layout) and
 * append the anchor as its last child so related products render inline. */
function ensureAnchor() {
  let anchor = document.getElementById(ANCHOR_ID);
  if (anchor && anchor.isConnected) return anchor;

  // The ProductPage renders `<section className="pt-8 pb-16 ...">` as its root.
  // Prefer the [data-testid="product-media"]'s closest section (the actual page container).
  const media = document.querySelector('[data-testid="product-media"]');
  let container = null;
  if (media) {
    container = media.closest("section") || media.closest("main") || media.parentElement;
  }
  if (!container) container = document.querySelector("main") || document.body;

  anchor = document.createElement("div");
  anchor.id = ANCHOR_ID;
  container.appendChild(anchor);
  return anchor;
}

function removeAnchor() {
  const el = document.getElementById(ANCHOR_ID);
  if (el) el.remove();
}

function ProductCard({ p }) {
  const testid = `jo-related-product-${p.slug}`;
  return (
    <Link
      to={`/shop/${p.slug}`}
      data-testid={testid}
      className="group block bg-white border border-neutral-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
    >
      <div className="aspect-square bg-neutral-100 overflow-hidden">
        {p.featured_image ? (
          <img
            src={p.featured_image}
            alt={p.alt_text || p.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : null}
      </div>
      <div className="p-3">
        <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
          {p.category || ""}
        </div>
        <div className="text-sm font-semibold text-neutral-900 line-clamp-2 min-h-[2.5rem]">
          {p.name}
        </div>
        <div className="mt-2 flex items-baseline gap-1">
          <span className="text-base font-bold text-[color:var(--brand-red,#a06a2f)]">
            {fmtEur(p.price_cents)}
          </span>
          {p.unit ? (
            <span className="text-xs text-neutral-500">/ {p.unit}</span>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

function Section({ title, subtitle, products, testid }) {
  if (!products || products.length === 0) return null;
  return (
    <section className="py-10 border-t border-neutral-200" data-testid={testid}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-6">
          <h2 className="text-2xl font-serif font-bold text-neutral-900">{title}</h2>
          {subtitle ? (
            <p className="text-sm text-neutral-600 mt-1">{subtitle}</p>
          ) : null}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {products.slice(0, 4).map((p) => (
            <ProductCard key={p.slug} p={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function JORelatedProductsInjector() {
  const location = useLocation();
  const currentSlug = useProductRoute(location.pathname);
  const [allProducts, setAllProducts] = useState([]);
  const [current, setCurrent] = useState(null);
  const [anchor, setAnchor] = useState(null);

  // Fetch products when we hit a product page
  useEffect(() => {
    if (!currentSlug) {
      setAllProducts([]);
      setCurrent(null);
      removeAnchor();
      setAnchor(null);
      return;
    }
    let alive = true;
    axios.get(`${API}/shop/products`).then((r) => {
      if (!alive) return;
      const list = Array.isArray(r.data) ? r.data : [];
      setAllProducts(list);
      const cur = list.find((p) => p.slug === currentSlug) || null;
      setCurrent(cur);
      if (cur) pushRecent(cur.slug);
    }).catch(() => {});
    return () => { alive = false; };
  }, [currentSlug]);

  // Ensure the anchor is present in the DOM once the page has rendered
  useEffect(() => {
    if (!currentSlug) return;
    let tries = 0;
    const findAnchor = () => {
      // Only proceed once the ProductPage's [data-testid="product-media"] is mounted
      if (document.querySelector('[data-testid="product-media"]')) {
        const el = ensureAnchor();
        setAnchor(el);
        return true;
      }
      return false;
    };
    if (findAnchor()) return;
    const interval = setInterval(() => {
      tries++;
      if (findAnchor() || tries > 30) clearInterval(interval);
    }, 200);
    return () => clearInterval(interval);
  }, [currentSlug]);

  const relatedProducts = useMemo(() => {
    if (!current || allProducts.length === 0) return [];
    return allProducts
      .filter((p) => p.slug !== current.slug && p.category === current.category)
      .slice(0, 4);
  }, [allProducts, current]);

  const recentProducts = useMemo(() => {
    if (!current || allProducts.length === 0) return [];
    const recentSlugs = getRecent().filter((s) => s !== current.slug);
    const map = new Map(allProducts.map((p) => [p.slug, p]));
    return recentSlugs.map((s) => map.get(s)).filter(Boolean).slice(0, 4);
  }, [allProducts, current, location.pathname]);

  if (!anchor || !current) return null;

  return createPortal(
    <>
      <Section
        testid="jo-related-similar"
        title="Vous aimerez aussi"
        subtitle={`D'autres produits de la catégorie ${current.category || ""} sélectionnés pour vous.`}
        products={relatedProducts}
      />
      <Section
        testid="jo-related-recent"
        title="Vus récemment"
        subtitle="Reprenez votre visite là où vous l'avez laissée."
        products={recentProducts}
      />
    </>,
    anchor
  );
}
